import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, Observable, of, PartialObserver, share, Subscription, switchMap, take } from 'rxjs';

import { MonitoringLogger } from '@fcom/core';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { retryWithBackoff, snapshot } from '@fcom/rx';
import { unsubscribe } from '@fcom/core/utils/unsubscribe';
import { FinnairAirBoundsRequest } from '@fcom/dapi/api/models/finnair-air-bounds-request';
import { FinnairAirBoundsResponse } from '@fcom/dapi/api/models/finnair-air-bounds-response';
import { FinnairItineraryItemFlight } from '@fcom/dapi/api/models/finnair-itinerary-item-flight';
import { AirOffersStatus } from '@fcom/common/interfaces/booking/air-offers-state.interface';
import { BookingAppState } from '@fcom/common/interfaces/booking/booking-state.interface';
import { TicketSelectionParams } from '@fcom/common/interfaces/booking/booking-air-bounds.interface';
import * as BookingWidgetActions from '@fcom/booking-widget/store/actions/booking-widget.actions';
import { NotificationWarning } from '@fcom/booking-widget/interfaces/notification-data.interface';
import * as BoundsActions from '@fcom/common/store/actions/bounds.actions';
import { selectGlobalBookingTripType } from '@fcom/common/store/selectors/global-booking.selectors';
import { CommonAirBoundsService } from '@fcom/common/services/common-air-bounds/common-air-bounds.service';
import {
  airBoundsRequest,
  boundsStatus,
  lastRequestParams,
  selectedCheapestOffer,
  selectedInboundBound,
  serializedLastRequestParams,
} from '@fcom/common/store/selectors/bounds.selector';
import { LoginStatus } from '@fcom/core-api/login/interfaces/login.interface';
import { DapiService } from '@fcom/dapi/api/dapi.service';

import { findAirBounds } from '../../../../dapi/api/fn/offers/find-air-bounds';
import { mapAirBoundIdToBound, mapCityNameAndAirportToBounds } from '../../../utils/common-data.utils';

const NO_FLIGHTS_STATUSES: string[] = ['NO_FLIGHTS_FOUND', 'NO_FLIGHTS_LEFT_FOR_TODAY'];
const DENIED_BY_AKAMAI = /You don't have permission to access/;

@Injectable()
export class BookingAirBoundsService implements OnDestroy {
  private configService = inject(ConfigService);
  private store$ = inject<Store<BookingAppState>>(Store);
  private monitoringLogger = inject(MonitoringLogger);
  private dapiService = inject(DapiService);
  private commonAirBoundsService = inject(CommonAirBoundsService);

  static NUMBER_OF_RETRIES = 1;

  private subscription: Subscription;
  private upsellSubscription: Subscription;
  tripType$: Observable<TripType>;
  _preselectedBoundId$: BehaviorSubject<string> = new BehaviorSubject(null);

  constructor() {
    this.tripType$ = this.store$.select(selectGlobalBookingTripType);
  }

  get preselectedBoundId$(): Observable<string> {
    return this._preselectedBoundId$.asObservable();
  }

  ngOnDestroy(): void {
    this.subscription = unsubscribe(this.subscription);
    this.upsellSubscription = unsubscribe(this.upsellSubscription);
  }

  setPreselectedBoundId(boundId: string): void {
    this._preselectedBoundId$.next(boundId);
  }

  resetPreselectedBoundId(): void {
    if (this._preselectedBoundId$.getValue()) {
      this._preselectedBoundId$.next(null);
    }
  }

  /**
   * Triggers bound fetching for the given parameters. Stores the result
   * in the state tree under {@link AirBoundsState}
   */
  triggerFetchBounds(params: TicketSelectionParams, loginStatus: LoginStatus): void {
    if (this.subscription) {
      this.subscription = unsubscribe(this.subscription);
    }
    const serialized: string = snapshot(this.store$.pipe(serializedLastRequestParams()));
    const boundStatus: AirOffersStatus = snapshot(this.store$.pipe(boundsStatus()));

    const currentParamsSerialized = JSON.stringify(params);

    // TODO: we need to fetch bounds again when they expire, even if the params stay the same
    if (boundStatus === AirOffersStatus.OK && serialized === currentParamsSerialized) {
      return;
    }

    this.store$.dispatch(BoundsActions.startLoadingOutbounds());
    this.store$.dispatch(BoundsActions.setLastRequestParams({ params }));

    this.subscription = this.commonAirBoundsService
      .fetchAndStoreOutboundsToCache(params, loginStatus)
      .pipe(take(1))
      .subscribe(this.handleBoundResponse(BoundsActions.setOutbounds));
  }

  /**
   * Retrieves the return bounds for a given outbound id.
   *
   * @param selectedOutboundId the selected outbound id
   * @param selectedFareFamilyCode the selected outbound fare family code
   */
  getReturnBounds(selectedOutboundId: string, selectedFareFamilyCode: string): void {
    const requestPayload$ = this.buildReturnBoundsRequest(selectedOutboundId, selectedFareFamilyCode);
    if (this.subscription) {
      this.subscription = unsubscribe(this.subscription);
    }
    this.store$.dispatch(BoundsActions.startLoadingInbounds());
    this.subscription = requestPayload$
      .pipe(
        switchMap((requestPayload) => this.getBounds(requestPayload)),
        retryWithBackoff(BookingAirBoundsService.NUMBER_OF_RETRIES)
      )
      .subscribe(this.handleBoundResponse(BoundsActions.setInbounds));
  }

  /**
   * Fetches return bounds for the ECLASIC1 (classic) upgrade fare family in parallel to the
   * primary ESLIGHT1 inbound request. Stores the response in the NgRx store under `upsellBounds`
   * and auto-selects the matching classic inbound bound via `selectedUpsellBoundAirBoundId`.
   *
   * @param selectedOutboundId the ECLASIC1 outbound fare-family id (used as selectedBound.id in the request)
   * @param selectedFareFamilyCode e.g. 'ECLASIC1'
   */
  getReturnUpsellBounds(selectedOutboundId: string, selectedFareFamilyCode: string): void {
    const upsellRequestPayload$ = this.buildReturnBoundsRequest(selectedOutboundId, selectedFareFamilyCode, false);

    if (this.upsellSubscription) {
      this.upsellSubscription = unsubscribe(this.upsellSubscription);
    }

    this.upsellSubscription = upsellRequestPayload$
      .pipe(
        switchMap((upsellRequestPayload) => this.getBounds(upsellRequestPayload)),
        retryWithBackoff(BookingAirBoundsService.NUMBER_OF_RETRIES)
      )
      .subscribe({
        next: (data) => {
          if (NO_FLIGHTS_STATUSES.indexOf(data.status) === -1) {
            this.store$.dispatch(BoundsActions.setUpsellBounds({ bounds: mapCityNameAndAirportToBounds(data) }));
            this.preSelectUpsellInboundIfFound(data, selectedFareFamilyCode);
          }
          this.upsellSubscription = unsubscribe(this.upsellSubscription);
        },
        error: () => {
          this.upsellSubscription = unsubscribe(this.upsellSubscription);
        },
        complete: () => {
          this.upsellSubscription = unsubscribe(this.upsellSubscription);
        },
      });
  }

  /**
   * Dispatch action to set bound id if conditions met
   *
   * @param action kind of action
   * @param requestParams params from last request
   * @param bounds received bounds
   */
  handleFlightPreSelection(
    action: typeof BoundsActions.setOutbounds | typeof BoundsActions.setInbounds,
    bounds: FinnairAirBoundsResponse
  ): void {
    const requestParams = this.getLastRequestParams();
    const isValidParams = this.validateFlightPreSelectionParams(requestParams);
    const isTwoWay =
      isValidParams &&
      requestParams.flights?.length === 2 &&
      Boolean(requestParams.departureFlightNumbers) &&
      Boolean(requestParams.returnFlightNumbers);

    if (
      isValidParams &&
      action === BoundsActions.setOutbounds &&
      ((!isTwoWay && requestParams.departureFlightNumbers) || isTwoWay)
    ) {
      this.preSelectOutboundIfFound(bounds, requestParams);
    } else if (isValidParams && action === BoundsActions.setInbounds && isTwoWay) {
      this.preSelectInboundIfFound(bounds, requestParams);
    }
  }

  /**
   * Get last request params from store
   *
   * @returns {TicketSelectionParams} object
   */
  getLastRequestParams(): TicketSelectionParams {
    return snapshot(this.store$.pipe(lastRequestParams()));
  }

  private buildReturnBoundsRequest(
    selectedOutboundId: string,
    selectedFareFamilyCode: string,
    enableInstantSearchMonitoring = true
  ): Observable<FinnairAirBoundsRequest> {
    const originalRequest: FinnairAirBoundsRequest = snapshot(this.store$.pipe(airBoundsRequest()));
    const isSelectedCheapestOffer: boolean = snapshot(this.store$.pipe(selectedCheapestOffer()));
    const trackInstantSearch = isSelectedCheapestOffer && enableInstantSearchMonitoring;

    const itineraries = [
      Object.assign({}, originalRequest.itineraries[0], { isRequestedBound: false }),
      Object.assign({}, originalRequest.itineraries[1], { isRequestedBound: true }),
    ];

    const request: FinnairAirBoundsRequest = Object.assign({}, originalRequest, {
      itineraries,
      selectedBound: { id: selectedOutboundId, fareFamilyCode: selectedFareFamilyCode },
    });

    return trackInstantSearch ? this.commonAirBoundsService.withMonitoringPrices(request) : of(request);
  }

  /**
   * Get flight bounds for a certain date.
   *
   * @param params the search parameters to use
   * @returns {Observable<FinnairAirBoundsResponse>} the fetched bounds
   */
  private getBounds(params: FinnairAirBoundsRequest): Observable<FinnairAirBoundsResponse> {
    return this.dapiService
      .invoke$(findAirBounds, this.configService.cfg.offersUrl, {
        body: params,
      })
      .pipe(share());
  }

  private handleBoundResponse(
    action: typeof BoundsActions.setOutbounds | typeof BoundsActions.setInbounds
  ): PartialObserver<FinnairAirBoundsResponse> {
    return {
      next: (data) => {
        if (NO_FLIGHTS_STATUSES.indexOf(data.status) !== -1) {
          this.store$.dispatch(BoundsActions.noFlightsFound());
          this.store$.dispatch(
            BookingWidgetActions.setNotificationWarning({
              key: NotificationWarning.NO_FLIGHTS,
              isActive: true,
            })
          );
        } else {
          this.store$.dispatch(action({ bounds: mapCityNameAndAirportToBounds(data) }));

          this.handleFlightPreSelection(action, data);
        }
        this.subscription = unsubscribe(this.subscription);
      },
      error: (error: HttpErrorResponse) => {
        const status: string = error?.error?.status;

        if (status === 'INVALID_INPUT') {
          this.store$.dispatch(BoundsActions.invalidInput());
        } else {
          if (!DENIED_BY_AKAMAI.test(error.error)) {
            this.monitoringLogger.error(`Error fetching bounds: ${error.status} ${error.statusText}`, { error });
          }
          this.store$.dispatch(BoundsActions.error());
        }
        this.subscription = unsubscribe(this.subscription);
      },
      complete: () => {
        this.subscription = unsubscribe(this.subscription);
      },
    };
  }

  private isValidFlightNumberFormat(value: string): boolean {
    // expects that the first 2 Characters are upper case letters
    // followed by 2 to 4 digits
    // ex. AY123
    return /^([A-Z]{2})(\d{2,4})$/.test(value);
  }

  private validateFlightPreSelectionParams(requestParams: TicketSelectionParams | undefined): boolean {
    if (!requestParams) {
      return false;
    }

    const { departureFlightNumbers, returnFlightNumbers } = requestParams;

    return [departureFlightNumbers, returnFlightNumbers].every((item) => {
      return item ? item.split(',').every((token) => this.isValidFlightNumberFormat(token.toUpperCase())) : true;
    });
  }

  private preSelectOutboundIfFound(bounds: FinnairAirBoundsResponse, requestParams: TicketSelectionParams): void {
    const { departureFlightNumbers, departureFareFamily } = requestParams;
    const airBoundId = mapAirBoundIdToBound(
      bounds,
      departureFlightNumbers.toUpperCase(),
      departureFareFamily?.toUpperCase()
    );
    if (airBoundId) {
      this.store$.dispatch(BoundsActions.setOutboundAirBoundId({ outboundAirBoundId: airBoundId }));
    }
  }

  private preSelectInboundIfFound(bounds: FinnairAirBoundsResponse, requestParams: TicketSelectionParams): void {
    const { returnFlightNumbers, returnFareFamily } = requestParams;
    const airBoundId = mapAirBoundIdToBound(bounds, returnFlightNumbers.toUpperCase(), returnFareFamily?.toUpperCase());
    if (airBoundId) {
      this.store$.dispatch(BoundsActions.setInboundAirBoundId({ inboundAirBoundId: airBoundId }));
    }
  }

  private preSelectUpsellInboundIfFound(bounds: FinnairAirBoundsResponse, selectedFareFamilyCode: string): void {
    this.store$.pipe(selectedInboundBound(), filter(Boolean), take(1)).subscribe((currentInboundBound) => {
      const flightNumbers = currentInboundBound?.details?.itinerary
        ?.map((i) => (i as FinnairItineraryItemFlight)?.flightNumber)
        .filter(Boolean)
        .join(', ');

      if (!flightNumbers) {
        return;
      }

      const upsellBoundId = mapAirBoundIdToBound(bounds, flightNumbers.toUpperCase(), selectedFareFamilyCode);

      if (upsellBoundId) {
        this.store$.dispatch(BoundsActions.setUpsellBoundAirBoundId({ upsellBoundAirBoundId: upsellBoundId }));
      }
    });
  }
}
